import { type JSX, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizations } from 'Api/UcrmTprm/UcrmTprmApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ControlTable } from 'Components/ControlTable/ControlTable';
import { ExceptionHistoryTable, ExceptionHistoryTableProps } from 'Components/Exceptions/ExceptionHistoryTable/ExceptionHistoryTable';
import { AssociatedControlsModal } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { PrimaryTabs, Tab } from 'Components/Tabs/PrimaryTabs/PrimaryTabs';
import { Text } from 'Components/Text/Text';
import { OrganizationSummary } from 'Components/UcrmTprm/OrganizationSummary/OrganizationSummary';
import { ICON_DOWNLOAD, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { EXCEPTIONS, ISSUES_EXCEPTIONS } from 'Config/Paths';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { downloadElementAsPdf } from 'Helpers/PdfUtils';
import { getUpdateExceptionUrl, pathPrefixForIssuesExceptionsModule } from 'Helpers/URLBuilder/URLBuilder';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { ExceptionHistoryResponse, ExceptionResponse, ExceptionStatus } from 'Models/Exceptions';
import { OperationalControl } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ThirdPartyResponseWithServices } from 'Models/TPRM';
import { BusinessResponseWithUseCases } from 'Models/Ucrm';
import styles from 'Styles/DetailsPage.module.css';

import { ExceptionDetailsSnapshot } from '../Components/ExceptionDetailsSnapshot/ExceptionDetailsSnapshot';
import { ExceptionHistoryModal } from '../Components/ExceptionHistoryModal/ExceptionHistoryModal';

enum ExceptionTab {
    CONTROLS = 'Controls',
    ORGANIZATION = 'Third Party',
    HISTORY = 'History',
}

interface UrlParams {
    exceptionId: string;
}

interface ExceptionDetailsOperationalControlsProps {
    authHandler: AuthHandler;
    type: ProductModule.CONTROLS;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    moduleApi?: never; // Typed like this so that `moduleApi` can be used unconditionally as a `useEffect` dependency.
}

interface ExceptionDetailsDueDiligenceProps {
    authHandler: AuthHandler;
    type: DueDiligenceModule;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export type ExceptionDetailsProps = ExceptionDetailsOperationalControlsProps | ExceptionDetailsDueDiligenceProps;
/**
 * Renders a page of detailed information for a specific exception.
 * This includes: current details; a tab containing a table of historical snapshots; and, depending on the exception type, a tab containing either a table of impacted controls or a summary of the impacted third party.
 */
export const ExceptionDetails = (props: ExceptionDetailsProps) => {
    const cachedData = useCachedData();
    const { exceptionId } = useParams<keyof UrlParams>() as UrlParams;
    const printPdfRef = useRef<HTMLDivElement>(null);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [exception, setException] = useState<ExceptionResponse>();
    const [organizationIdToOrganizationMap, setOrganizationIdToOrganizationMap] = useState<Map<string, ThirdPartyResponseWithServices | BusinessResponseWithUseCases>>(); // This isn't actually used when the exception belongs to the Operational Controls module.
    const [exceptionHistory, setExceptionHistory] = useState<ExceptionHistoryResponse[]>();
    const [exceptionHistoryToShowInModal, setExceptionHistoryToShowInModal] = useState<ExceptionHistoryResponse>();
    const [impactedControlsToShow, setImpactedControlsToShow] = useState<OperationalControl[]>();

    useEffect(() => {
        const getExceptionDetails = async (exceptionId: string): Promise<void> => {
            try {
                const getExceptionDetailsResponse = await props.exceptionsApi.getException(exceptionId);
                const exception = getExceptionDetailsResponse.data;
                setException(exception);
            } catch (error) {
                handleRequestError(error);
            }
        };

        getExceptionDetails(exceptionId);
    }, [exceptionId, props.exceptionsApi]);

    useEffect(() => {
        const getExceptionHistory = async (exceptionId: string): Promise<void> => {
            try {
                const response = await props.exceptionsApi.getExceptionHistory(exceptionId);
                setExceptionHistory(response.data);
            } catch (error) {
                handleRequestError(error);
            }
        };

        getExceptionHistory(exceptionId);
    }, [exceptionId, props.exceptionsApi]);

    useEffect(() => {
        const fetchOrganizations = async (): Promise<void> => {
            if (props.type === ProductModule.CONTROLS) {
                setOrganizationIdToOrganizationMap(new Map());
                return;
            }

            try {
                // TODO: `getOrganizationDetails` should be used if/when `ThirdPartyResponseWithServices`/`BusinessResponseWithUseCases` and `OrganizationResponseBase` are consolidated.
                const response = await getOrganizations(props.moduleApi);
                setOrganizationIdToOrganizationMap(new Map(response.data.map((organization) => [organization.id, organization])));
            } catch (error) {
                handleRequestError(error);
            }
        };

        fetchOrganizations();
    }, [props.moduleApi, props.type]);

    const handleRequestError = (error: Error): void => setErrorMessage(error.message);

    if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    }

    if (!(exception && exceptionHistory && organizationIdToOrganizationMap)) {
        return <Placeholder />;
    }

    /** Convert a component to a PDF and download it. */
    const handleDownloadPdf = async () => {
        const elementToPrint = printPdfRef.current;
        if (!elementToPrint) {
            return;
        }

        let filename;
        if (exception.impacted_vendor) {
            filename = `${organizationIdToOrganizationMap.get(exception.impacted_vendor!)!.name} - ${exception.title}.pdf`;
        } else {
            filename = `${exception.title}.pdf`;
        }

        const loggedInUser = props.authHandler.getAuthenticatedUserFullName(cachedData.users);

        await downloadElementAsPdf(elementToPrint, filename, loggedInUser);
    };

    const buttons: JSX.Element = (() => {
        switch (exception.status) {
            case ExceptionStatus.DRAFT_OPEN:
                return (
                    <>
                        <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                            Download
                        </Button>
                        <Link variant="primaryButton" to={getUpdateExceptionUrl(exception.id, exception.type, false)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Exception
                        </Link>
                    </>
                );
            case ExceptionStatus.APPROVED:
                return (
                    <>
                        <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                            Download
                        </Button>
                        <Link variant="secondaryButton" to={getUpdateExceptionUrl(exception.id, exception.type, false)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Exception
                        </Link>
                        <Link variant="primaryButton" to={getUpdateExceptionUrl(exception.id, exception.type, true)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Draft Closure
                        </Link>
                    </>
                );
            case ExceptionStatus.DRAFT_CLOSE:
                return (
                    <>
                        <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                            Download
                        </Button>
                        <Link variant="primaryButton" to={getUpdateExceptionUrl(exception.id, exception.type, true)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Exception
                        </Link>
                    </>
                );
            case ExceptionStatus.CLOSED:
                return (
                    <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                        Download
                    </Button>
                );
        }
    })();

    const getOrganizationName = (organizationId: string) => organizationIdToOrganizationMap.get(organizationId)!.name;

    const exceptionHistoryTableProps: ExceptionHistoryTableProps = (() => {
        switch (exception.type) {
            case ProductModule.CONTROLS:
                return { type: ProductModule.CONTROLS, histories: exceptionHistory, displayMappedControlsModal: setImpactedControlsToShow, onSelectHistory: (history: ExceptionHistoryResponse) => setExceptionHistoryToShowInModal(history) };
            case ProductModule.TPRM:
            case ProductModule.UCRM:
                return { type: exception.type, histories: exceptionHistory, getOrganizationName: getOrganizationName, onSelectHistory: (history: ExceptionHistoryResponse) => setExceptionHistoryToShowInModal(history) };
        }
    })();

    return (
        <>
            {exceptionHistoryToShowInModal && <ExceptionHistoryModal history={exceptionHistoryToShowInModal} documentApi={props.documentApi} hideModal={() => setExceptionHistoryToShowInModal(undefined)} />}
            {impactedControlsToShow && <AssociatedControlsModal associatedControls={impactedControlsToShow} hideModal={() => setImpactedControlsToShow(undefined)} />}

            <div ref={printPdfRef}>
                <PageLayoutDefault
                    headerBreadcrumb={
                        <Breadcrumb textColor="blue">
                            <BreadcrumbLink link={`${pathPrefixForIssuesExceptionsModule(exception.type)}/${ISSUES_EXCEPTIONS}#${EXCEPTIONS}`}>Exceptions</BreadcrumbLink>
                            <BreadcrumbText>{exception.title}</BreadcrumbText>
                        </Breadcrumb>
                    }
                    headerTitle={exception.title}
                    headerButtons={buttons}
                    headerDescription={exception.status === ExceptionStatus.CLOSED ? `Closed: ${iso8601ToUsDateShort(exception.closed_timestamp!)} by ${getUserNameFromSubject(exception.closed_by!, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}` : `Last updated: ${iso8601ToUsDateShort(exception.last_updated_timestamp)} by ${getUserNameFromSubject(exception.last_updated_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}`}
                    body={[
                        {
                            title: 'Exception Details',
                            content: <ExceptionDetailsSnapshot documentApi={props.documentApi} exceptionResponse={exception} type="current" />,
                        },
                        {
                            content: (
                                <PrimaryTabs defaultActiveTab={exception.type === ProductModule.CONTROLS ? ExceptionTab.CONTROLS : ExceptionTab.ORGANIZATION} removePadding transparent>
                                    {exception.type === ProductModule.CONTROLS ? (
                                        <Tab eventKey={ExceptionTab.CONTROLS} title={ExceptionTab.CONTROLS}>
                                            <div className={styles.tabContent}>
                                                <ControlTable controls={exception.impacted_controls} />
                                            </div>
                                        </Tab>
                                    ) : (
                                        <Tab eventKey={ExceptionTab.ORGANIZATION} title={exception.type === ProductModule.UCRM ? 'Business' : ExceptionTab.ORGANIZATION}>
                                            <div className={styles.tabContent}>
                                                <Text variant="Header2" color="blue" noStyles>
                                                    {organizationIdToOrganizationMap.get(exception.impacted_vendor!)!.name}
                                                </Text>
                                                <hr />
                                                <OrganizationSummary module={exception.type} organization={organizationIdToOrganizationMap.get(exception.impacted_vendor!)!} />
                                            </div>
                                        </Tab>
                                    )}
                                    <Tab eventKey={ExceptionTab.HISTORY} title={ExceptionTab.HISTORY}>
                                        <div className={styles.tabContent} data-testid="exceptionHistoryTable">
                                            {exception.status !== ExceptionStatus.DRAFT_OPEN ? <ExceptionHistoryTable {...exceptionHistoryTableProps} /> : <Text>History will not be recorded until the exception is approved.</Text>}
                                        </div>
                                    </Tab>
                                </PrimaryTabs>
                            ),
                        },
                    ]}
                />
            </div>
        </>
    );
};
